import { PWA_PROMPT_STORAGE_KEYS } from '~/components/PWA/pwaPromptConfig';
import {
  acknowledgeHash,
  type CommunityPushContext,
  fetchPushRequest,
  getCommunityPushContext,
  getCommunityPushRegistration,
  isCommunityPushPath,
  isStandaloneCommunity,
  readAcknowledgedHash,
  withCommunityPushLock,
} from '~/utils/communityAppAdoption';
import { NectarPageBlock } from '~/utils/nectarPageBlock';
import { getOrCreateAnonymousUserId } from '~/utils/nectarTracking';

// Client-side web push helpers, split into browser (Web Push API) and server
// (orchid /push/* actions) halves. Distinct from services/api/push, which is
// server-side Swarm integration.

// Browser: create a push subscription with the given VAPID key. Returns null
// when push is unavailable (no service worker / PushManager).
export const createBrowserPushSubscription = async (applicationServerKey: string): Promise<PushSubscription | null> => {
  if (!('serviceWorker' in navigator)) return null;

  const registration = await (isCommunityPushPath()
    ? getCommunityPushRegistration()
    : navigator.serviceWorker.getRegistration());
  if (!registration?.pushManager) return null;

  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey,
  });
};

// Browser: remove the active push subscription. Returns the removed subscription
// (so the caller can tell the server), or null when there was nothing to remove.
export const removeBrowserPushSubscription = async (): Promise<PushSubscription | null> => {
  if (!('serviceWorker' in navigator)) return null;

  const registration = await (isCommunityPushPath()
    ? getCommunityPushRegistration()
    : navigator.serviceWorker.getRegistration());
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return null;

  if (!(await subscription.unsubscribe())) throw new Error('Push unsubscribe failed');

  return subscription;
};

// Server: register the subscription with the backend. Returns whether it was accepted.
const pushRequest = (
  subscription: PushSubscription,
  context?: CommunityPushContext,
  surface?: CommunityPushContext['pageBlock'],
) => ({
  headers: { 'Content-Type': 'application/json', ...(context ? { 'X-CSRF-Token': context.csrfToken } : {}) },
  body: JSON.stringify(
    context || surface
      ? {
          subscription,
          ...(context ? { community_context: true, previous_hash: readAcknowledgedHash(context.storageKey) } : {}),
          nectar_page_block: surface ?? context?.pageBlock,
          nectar_anonymous_user_id: getOrCreateAnonymousUserId(),
          nectar_is_pwa: isStandaloneCommunity(),
        }
      : subscription,
  ),
});

export const registerPushSubscription = async (
  subscription: PushSubscription,
  context = getCommunityPushContext(),
): Promise<boolean> => {
  const response = await fetchPushRequest('/push/subscribe', {
    method: 'POST',
    ...pushRequest(subscription, context),
  });

  if (response.ok && context) {
    const result = await response.json();
    if (typeof result.data_hash !== 'string') return false;
    acknowledgeHash(context.storageKey, result.data_hash);
  }
  return response.ok;
};

// Server: delete the subscription from the backend. Returns whether it succeeded.
export const unregisterPushSubscription = async (
  subscription: PushSubscription,
  context: CommunityPushContext | null | undefined = getCommunityPushContext(),
  surface?: CommunityPushContext['pageBlock'],
): Promise<boolean> => {
  const response = await fetchPushRequest('/push/unsubscribe', {
    method: 'POST',
    ...pushRequest(subscription, context ?? undefined, surface),
  });

  if (response.ok && context) acknowledgeHash(context.storageKey, null);
  return response.ok;
};

// Per-user prompt dismissals the next member on this device should see fresh.
// The `*Installed` flag is device-level and intentionally left in place.
const COMMUNITY_PROMPT_KEYS_TO_RESET = [
  PWA_PROMPT_STORAGE_KEYS.communityPush,
  PWA_PROMPT_STORAGE_KEYS.communityInstall,
  PWA_PROMPT_STORAGE_KEYS.communityInstallCallout,
];

// Logout teardown: reset the community prompt dismissals and unsubscribe so a
// logged-out or next user stops getting the previous member's notifications.
// Runs while still authenticated (auth-gated) and never throws — can't block logout.
export const tearDownPush = async (): Promise<void> => {
  // Reset unconditionally — a user who dismissed a prompt without ever
  // subscribing/installing should still reset it for the next member.
  for (const storageKey of COMMUNITY_PROMPT_KEYS_TO_RESET) {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // localStorage may be unavailable.
    }
  }

  try {
    sessionStorage.removeItem(PWA_PROMPT_STORAGE_KEYS.communityInstallCalloutVisit);
  } catch {
    // sessionStorage may be unavailable.
  }

  try {
    await withCommunityPushLock(async () => {
      const context = getCommunityPushContext();
      if ('serviceWorker' in navigator) {
        let registrations: readonly ServiceWorkerRegistration[];
        if (navigator.serviceWorker.getRegistrations) {
          registrations = await navigator.serviceWorker.getRegistrations();
        } else {
          const registration = await navigator.serviceWorker.getRegistration();
          registrations = registration ? [registration] : [];
        }
        for (const registration of registrations) {
          const path = registration.scope ? new URL(registration.scope).pathname.replace(/\/$/, '') : '';
          if (path !== '' && path !== '/community') continue;
          const subscription = await registration.pushManager?.getSubscription();
          if (!subscription) continue;
          if (!(await subscription.unsubscribe())) continue;
          await unregisterPushSubscription(
            subscription,
            path === '/community' ? context : null,
            NectarPageBlock.CommunityAppLogout,
          );
        }
      }
      if (context) {
        const hash = readAcknowledgedHash(context.storageKey);
        if (hash) {
          const response = await fetchPushRequest('/push/unsubscribe', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': context.csrfToken },
            body: JSON.stringify({
              community_context: true,
              data_hash: hash,
              nectar_page_block: NectarPageBlock.CommunityAppLogout,
              nectar_anonymous_user_id: getOrCreateAnonymousUserId(),
              nectar_is_pwa: isStandaloneCommunity(),
            }),
          });
          if (response.ok) acknowledgeHash(context.storageKey, null);
        }
      }
    });
  } catch {
    // Best-effort.
  }
};
